import { AccountInfo } from "@azure/msal-browser";
import { ILocalizedString, ISystem, store, SystemModule } from "@comact/crc";
import _ from "lodash";
import { ValueOf } from "../common/types";

export const Levels = {
    Admin: "admin",
    Edit: "edit",
    View: "view",
    Denied: "denied",
} as const;

export type ILevel = ValueOf<typeof Levels>;

export const Permissions = {
    PmPackForecasting: {
        name: "pm-pack-forecasting",
        levels: [Levels.View, Levels.Edit],
    },
    Report: {
        name: "report",
        levels: [Levels.View],
    },
    ServiceRequest: {
        name: "service-request",
        levels: [Levels.View, Levels.Edit],
    },
    ResourceLibrary: {
        name: "resource-library",
        levels: [Levels.View],
    },
    ComponentsEngine: {
        name: "components-engine",
        levels: [Levels.View],
    },
    GrafanaIntegrationConfiguration: {
        name: "grafana-integration-configuration",
        levels: [Levels.Admin],
    },
    CloudBackups: {
        name: "cloud-backups",
        levels: [Levels.View],
    },
    SystemConfiguration: {
        name: "system-configuration",
        levels: [Levels.Admin],
    },
    ExternalAuthProviderConfiguration: {
        name: "external-authentication-provider-configuration",
        levels: [Levels.View, Levels.Edit],
    },
    NotificationManagement: {
        name: "notification-management",
        levels: [Levels.View, Levels.Edit, Levels.Admin],
    },
    ApiToken: {
        name: "api-token",
        levels: [Levels.View, Levels.Edit, Levels.Admin],
    },
    ScheduleWorkTeamConfiguration: {
        name: "schedule-work-team-configuration",
        levels: [Levels.View, Levels.Edit],
    },
    RoughInventoryTracking: {
        name: "rough-inventory-tracking",
        levels: [Levels.View, Levels.Edit, Levels.Admin],
    },
    CustomForms: {
        name: "custom-forms",
        levels: [Levels.View, Levels.Edit, Levels.Admin],
    },
    Odometers: {
        name: "odometers",
        levels: [Levels.View, Levels.Edit, Levels.Admin],
    },
    Downtimes: {
        name: "downtimes",
        levels: [Levels.View, Levels.Edit, Levels.Admin],
    },
    UserManagement: {
        name: "user-management",
        levels: [Levels.View, Levels.Edit, Levels.Admin],
    },
    GrafanaDashboard: {
        name: "grafana-dashboard",
        levels: [Levels.View, Levels.Edit, Levels.Admin],
    },
    DashboardAccess: {
        name: "dashboard-access",
        levels: [Levels.View],
    },
    SmartVision: {
        name: "smart-vision",
        levels: [Levels.View],
    },
    ELearning: {
        name: "e-learning",
        levels: [Levels.View],
    },
} as const;

export type IPermissions = typeof Permissions;

export interface IPermissionLevel {
    permission: IPermissions[keyof IPermissions];
    level: ILevel;
}

export const getPermissionKey = ({
    permission,
    level,
}: IPermissionLevel): IPermissionKey => `${permission.name}-${level}`;

const permissionsKeyToStruct = _.keyBy(Permissions, ({ name }) => name);
export const permissionNames = _.map(Permissions, ({ name }) => name);
export const getLevelsForPermissionKey = (name: string): ILevel[] => permissionsKeyToStruct[name] && [...permissionsKeyToStruct[name].levels];
export const getAllAvailableLevels = (name: string): ILevel[] => permissionsKeyToStruct[name] && [Levels.Denied, ...permissionsKeyToStruct[name].levels];

export type IPermissionName = IPermissions[keyof IPermissions]["name"];

export type IPermissionKey = `${IPermissionName}-${ILevel}`;

export const permissionKeys: IPermissionKey[] = _.flatMap(Permissions, ({
    name,
    levels,
}) => _.map(levels, (level): IPermissionKey => `${name}-${level}`));

export const getVisiblePermissions = (permissionsToFilter: IPermissionKey[]) => (
    _.filter(permissionsToFilter, (p) => _.includes(permissionKeys, p))
);

// Only used for CMOC
export interface IPermissionDetails {
    type: IPermissionKey;
    requirements: IPermissionKey[];
}

export type IPermissionsDetails = Record<string, IPermissionDetails>;

export interface IOrganization {
    readonly id: string;
    organizationNodeId: string; // node customer id (accessLevel)
    name: string;
    readonly creationDate: number;
    readonly modificationDate: number;
}

export interface IOrganizations {
    [id: string]: IOrganization;
}

export interface IGroup {
    readonly id: string;
    allSiteAccess: boolean; // equivalent to checking all millNodeIds, if true, millNodeIds have no effect
    siteNodeIds: string[] | null; // accessLevel, null for iep (always mill anyway)
    readonly organizationId: string;
    externalGroupId: string;
    name: ILocalizedString;
    permissions: IPermissionKey[];
    readonly readOnly: boolean; // admin and support group
    readonly hidden: boolean; // support group
    readonly creationDate: number;
    readonly modificationDate: number;
    readonly isExternal: boolean;
}

export interface IGroupForm extends IGroup {
    userIds: string[];
}

export interface IGroups {
    [id: string]: IGroup;
}

export interface IMyAccount {
    readonly id: string;
    username: string | null; // not used for ICP, mandatory for IEP
    email: string;
    phoneNumber: string;
    password: string | null; // always empty on read
}

export interface IUser {
    readonly id: string;
    readonly accountId: string;
    readonly organizationId: string;
    email: string | null; // optional on IEP, mandatory on ICP (readonly on icp?)
    password: string | null; // always empty on read
    username: string | null; // not used for ICP, mandatory for IEP
    phoneNumber: string | null;
    name: string | null; // name might not have been set yet on icp
    locale: ISystem["activeLocale"];
    userUnitSystems: IUserUnitSystems;
    theme: null | IStoreState["theme"];
    groupIds: string[];
    notificationMethods: ("email" | "sms")[];
    permissions: {
        [siteId: string]: {
            allowed: IPermissionKey[];
            denied: IPermissionKey[];
        };
    };
    readonly lastSeenDate: number | null;
    readonly hidden: boolean; // service user
    readonly readOnly: boolean; // service user
    deleted: boolean; // we must not delete a user, only archive it
    readonly creationDate: number;
    readonly modificationDate: number;
    readonly isExternal: boolean;
    favoriteDashboards: string[];
}

export interface IUserForm extends Omit<IUser, "groupIds"> {
    modificationDate: number; // to remove readonly (we want to update it when we change the password)
    passwordConfirm: string;
    internalGroupIds: string[];
    externalGroupIds: string[];
}

export interface IUsers {
    [id: string]: IUser;
}

export interface IUserUnitSystems {
    unitSystem: ISystem["unitSystem"];
    realUnitSystem: ISystem["realUnitSystem"];
    nominalUnitSystem: ISystem["nominalUnitSystem"];
}

export interface IPartialUser extends Pick<IUser, "name" | "id" | "modificationDate" | "email" | "deleted" | "username" | "hidden" | "isExternal" | "groupIds"> {
}

export interface IPartialUsers {
    [id: string]: IPartialUser;
}

export const createNewUser = (partialData?: (Partial<Omit<IUser, "id" | "userId" | "organizationId" | "creationDate" | "modificationDate">>)): IUser => ({
    id: null,
    username: "",
    name: "",
    email: "",
    phoneNumber: "",
    accountId: null, // the back will set it
    organizationId: null, // the back will set it depending on the header of the request
    password: "",
    groupIds: [],
    notificationMethods: ["email"],
    permissions: null,
    lastSeenDate: null,
    deleted: false,
    creationDate: null, // the back will set it
    modificationDate: null, // the back will set it
    favoriteDashboards: [],
    hidden: false,
    readOnly: false,
    locale: store.getState().system.defaultLocale,
    userUnitSystems: {
        unitSystem: SystemModule.selectors.getAllSystem(store.getState()).unitSystem,
        realUnitSystem: SystemModule.selectors.getAllSystem(store.getState()).realUnitSystem,
        nominalUnitSystem: SystemModule.selectors.getAllSystem(store.getState()).nominalUnitSystem,
    },
    isExternal: null,
    theme: null,
    ...partialData,
});

export const createNewGroup = (partialData?: (Partial<Omit<IGroup, "id" | "organizationId" | "creationDate" | "modificationDate">>)): IGroup => ({
    id: null,
    organizationId: null, // the back will set it depending on the header of the request
    externalGroupId: null,
    name: {
        values: {
            en: "",
            fr: "",
        },
    },
    allSiteAccess: false,
    siteNodeIds: [],
    permissions: [],
    hidden: false,
    readOnly: false,
    creationDate: null, // the back will set it
    modificationDate: null, // the back will set it
    isExternal: null,
    ...partialData,
});

/* eslint-disable camelcase */
export interface IUserMSAL extends AccountInfo {
    idTokenClaims: {
        aud: string;
        auth_time: number;
        emails: string[];
        exp: number;
        family_name: string;
        given_name: string;
        iat: number;
        iss: string;
        nbf: number;
        nonce: string;
        oid: string;
        sub: string;
        tfp: string;
        ver: string;
        extension_locale: "fr" | "en";
        extension_nominalUnitSystem: "imperial" | "metric";
        extension_realUnitSystem: "imperial" | "metric";
        extension_unitSystem: "imperial" | "metric";
    };
}

/* eslint-enable camelcase */
